import React, { useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Grid
} from '@material-ui/core';

const getValidationSchema = (isEdit) => {
    if (isEdit) {
        return Yup.object().shape({
            poids_d: Yup.number()
                .required('Poids donné obligatoire.')
                .typeError('Poids donné doit être un nombre.')
                .min(0, 'Le poids doit être positif.'),
            poids_r: Yup.number()
                .typeError('Poids retourné doit être un nombre.')
                .min(0, 'Le poids doit être positif.'),
            temps: Yup.number()
                .typeError('Temps doit être un nombre.')
                .min(0, 'Le temps doit être positif.'),
        });
    }
    
    return Yup.object().shape({
        poids_d: Yup.number()
            .required('Poids donné obligatoire.')
            .typeError('Poids donné doit être un nombre.')
            .min(0, 'Le poids doit être positif.'),
        poids_r: Yup.number()
            .required('Poids retourné obligatoire.')
            .typeError('Poids retourné doit être un nombre.')
            .min(0, 'Le poids doit être positif.'),
        temps: Yup.number()
            .required('Temps obligatoire.')
            .typeError('Temps doit être un nombre.')
            .min(0, 'Le temps doit être positif.'),
    });
};

const JoaillierEditModal = ({ 
    open, 
    rowData, 
    onClose, 
    onSave, 
    loading = false 
}) => {
    const poidsDRef = useRef(null);
    const isEdit = !!rowData?.id_ligne;

    const formik = useFormik({
        initialValues: {
            poids_d: rowData?.poids_d || '',
            poids_r: rowData?.poids_r || '',
            temps: rowData?.temps || '',
        },
        validationSchema: getValidationSchema(isEdit),
        onSubmit: async (values) => {
            try {
                await onSave({
                    id_ligne: rowData.id_ligne,
                    of: rowData.n_sachet,
                    poids: parseFloat(values.poids_d),
                    poidsR: parseFloat(values.poids_r),
                    temps: parseFloat(values.temps),
                    rendu: rowData.rendu,
                });
                onClose();
            } catch (error) {
                console.error('Erreur lors de la sauvegarde :', error);
            }
        },
    });

    const { values, errors, touched, handleChange, handleSubmit, handleBlur } = formik;

    useEffect(() => {
        if (open && poidsDRef.current) {
            setTimeout(() => poidsDRef.current?.focus(), 100);
        }
    }, [open]);

    useEffect(() => {
        if (rowData) {
            formik.setValues({
                poids_d: rowData.poids_d || '',
                poids_r: rowData.poids_r || '',
                temps: rowData.temps || '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Modifier le mouvement</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2} style={{ marginTop: 0 }}>
                        {/* Poids donné */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Poids donné (g)"
                                type="number"
                                inputProps={{ step: "0.01", min: "0" }}
                                name="poids_d"
                                value={values.poids_d}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.poids_d && !!errors.poids_d}
                                helperText={touched.poids_d ? errors.poids_d : 'Poids donné au joaillier'}
                                variant="outlined"
                                disabled={loading}
                                inputRef={poidsDRef}
                                size="small"
                            />
                        </Grid>

                        {/* Poids retourné */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Poids retourné (g)"
                                type="number"
                                inputProps={{ step: "0.01", min: "0" }}
                                name="poids_r"
                                value={values.poids_r}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.poids_r && !!errors.poids_r}
                                helperText={touched.poids_r ? errors.poids_r : 'Poids retourné par le joaillier'}
                                variant="outlined"
                                disabled={loading}
                                size="small"
                            />
                        </Grid>

                        {/* Temps */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Temps (h)"
                                type="number"
                                inputProps={{ step: "0.5", min: "0" }}
                                name="temps"
                                value={values.temps}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.temps && !!errors.temps}
                                helperText={touched.temps ? errors.temps : 'Temps de travail en heures'}
                                variant="outlined"
                                disabled={loading}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ padding: '16px' }}>
                    <Button onClick={onClose} disabled={loading}>
                        Annuler
                    </Button>
                    <Button 
                        type="submit" 
                        color="primary" 
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default JoaillierEditModal;
